import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { LuArrowRight, LuCheck, LuTriangleAlert } from 'react-icons/lu'
import SavingsCalculator from '../components/SavingsCalculator.jsx'
import {
  COMPETITORS,
  HIGH_STREET,
  KEYZEE_ROW,
  VERIFIED_AT_LABEL,
  getCompetitor,
} from '../data/competitors.js'
import NotFoundPage from './NotFoundPage.jsx'
import './HousePrices.css'
import './Compare.css'

const SITE = 'https://keyzee.co.uk'

/**
 * Per-competitor comparison landing page, e.g. /purplebricks-alternative/.
 *
 * Targets the highest commercial-intent query in the category ("<brand>
 * alternative"). The slug is passed in from the route table in App.jsx rather
 * than read from useParams, because react-router cannot express a `:slug`
 * segment with a static `-alternative` suffix — App.jsx maps over COMPETITORS.
 *
 * The static crawler HTML is produced at build time by scripts/prerender.mjs
 * (these routes are in its ROUTES list) — all data is bundled, so there is no
 * fetch to wait on and the Playwright capture gets the full page.
 *
 * @param {object} props
 * @param {string} props.slug - competitor slug from src/data/competitors.js
 */
const CompareAlternativePage = ({ slug }) => {
  const competitor = getCompetitor(slug)

  // Defensive: a route can only reach here via the COMPETITORS map, but a bad
  // manual edit shouldn't render a half-empty indexable page.
  if (!competitor) return <NotFoundPage />

  // The remaining fields (commission, contract, payWhen) are read off the row
  // objects by the comparison table below, so they are not destructured here.
  const { name, heroCost, heroHeadline, feeDetail, bestFor, summary, weaknesses, keyzeeEdge } = competitor

  const canonical = `${SITE}/${slug}-alternative/`
  const title = `${name} Alternative: Sell With 0% Commission and No Fees | Keyzee`
  const metaDescription = `Looking for a ${name} alternative? ${name} charges ${heroCost}. Keyzee is £0 — no listing fee, no commission, no contract. Compare the two before you commit.`

  const others = COMPETITORS.filter((c) => c.slug !== slug)

  const faqs = [
    {
      q: `Is there a free alternative to ${name}?`,
      a: `Yes. Keyzee is free to list and free to sell — no subscription, no fixed fee and 0% commission. ${name} charges ${feeDetail}. Keyzee makes its money from optional partner referrals (conveyancers, mortgage brokers, EPC assessors) if you choose to use them, never from the seller's sale.`,
    },
    {
      q: `How much does ${name} cost?`,
      a: `As checked on ${VERIFIED_AT_LABEL}, ${name} charges ${feeDetail}, with 0% commission on completion. Add-ons such as photography, floorplans and premium portal placement are usually priced separately. Check their site for current pricing before you decide.`,
    },
    {
      q: `What is the catch with a free platform like Keyzee?`,
      a: 'There is no fee and no commission — the trade-off is that you do the parts an agent would otherwise do: writing the listing, answering buyer messages and running your own viewings. If you would rather someone else handled all of that, a paid agent is the better fit and we would say so.',
    },
    {
      q: 'Do I still need a solicitor if I sell without an agent?',
      a: 'Yes. You are removing the estate agent, not the legal process. A conveyancer still handles the legal work once you have agreed a sale, on any of these platforms.',
    },
  ]

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Compare estate agents', item: `${SITE}/compare-estate-agents/` },
      { '@type': 'ListItem', position: 3, name: `${name} alternative`, item: canonical },
    ],
  }

  const rows = [KEYZEE_ROW, competitor, HIGH_STREET]

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`${name} alternative — sell with 0% commission | Keyzee`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${name} alternative — sell with 0% commission | Keyzee`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${SITE}/og-image.jpg`} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <section className="region-hero">
        <p className="search-hero__eyebrow">{name} alternative</p>
        <h1>{heroHeadline || `The free alternative to ${name}`}</h1>
        <p className="region-hero__lede">
          {/* Only our own figure is emphasised — heroCost can be a full clause
              rather than a price, and bolding that reads as shouting. */}
          {name} charges {heroCost} to sell your home. Keyzee charges <strong>£0</strong>. {keyzeeEdge}
        </p>
        <div className="region-cta__actions">
          <Link className="primary-button pill-action" to="/sell/new">
            List your home free <LuArrowRight aria-hidden="true" />
          </Link>
          <Link className="secondary-button pill-action" to="/compare-estate-agents/">
            Compare every option
          </Link>
        </div>
      </section>

      <section className="compare-table-wrap" aria-label={`Keyzee compared with ${name}`}>
        <h2>Keyzee vs {name}</h2>
        <div className="compare-table-scroll">
          <table className="compare-table">
            <thead>
              <tr>
                <th scope="col">&nbsp;</th>
                {rows.map((row) => (
                  <th key={row.name} scope="col" className={row.name === 'Keyzee' ? 'is-keyzee' : undefined}>
                    {row.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                ['What it costs', 'headlineFee'],
                ['Commission', 'commission'],
                ['Contract', 'contract'],
                ['When you pay', 'payWhen'],
                ['Best for', 'bestFor'],
              ].map(([label, key]) => (
                <tr key={key}>
                  <th scope="row">{label}</th>
                  {rows.map((row) => (
                    <td key={row.name} className={row.name === 'Keyzee' ? 'is-keyzee' : undefined}>
                      {row[key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="compare-verified">
          {name} pricing checked on {VERIFIED_AT_LABEL} against their published rates. Prices change — confirm
          on their site before deciding.
        </p>
      </section>

      <section className="region-section" aria-label={`About ${name}`}>
        <h2>What {name} actually is</h2>
        <p className="compare-prose">{summary}</p>
      </section>

      <SavingsCalculator initialPrice={250000} />

      <section className="region-section" aria-label={`Where ${name} costs you`}>
        <h2>Where {name} costs you</h2>
        <ul className="compare-points">
          {weaknesses.map((item) => (
            <li key={item.point}>
              <LuTriangleAlert aria-hidden="true" />
              <span>
                <strong>{item.point}.</strong> {item.detail}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="region-section" aria-label={`When ${name} is the better choice`}>
        <h2>When {name} is the better choice</h2>
        <p className="compare-prose">
          We would rather be useful than pushy, so: {bestFor.charAt(0).toLowerCase() + bestFor.slice(1)} will
          probably be happier with {name} than with us. Keyzee does not run your viewings, write your
          description or negotiate on your behalf. If you want those things done for you, paying for them is a
          perfectly sensible decision.
        </p>
        <p className="compare-prose">
          Keyzee is for the seller who is willing to speak to buyers directly — and would rather keep the
          commission than pay someone to stand between them.
        </p>
      </section>

      <section className="region-section" aria-label="Why Keyzee is free">
        <h2>Why Keyzee is free</h2>
        <ul className="sell-guide-checks">
          <li>
            <LuCheck aria-hidden="true" />
            <span>
              <strong>No seller fees at all.</strong> Listing is free, selling is free, and there is no
              subscription ticking away while your home is on the market.
            </span>
          </li>
          <li>
            <LuCheck aria-hidden="true" />
            <span>
              <strong>We earn from optional partners.</strong> If you choose a conveyancer, mortgage broker or
              EPC assessor through Keyzee, they pay us a referral fee. You are never obliged to use one.
            </span>
          </li>
          <li>
            <LuCheck aria-hidden="true" />
            <span>
              <strong>Buyers message you directly.</strong> Enquiries, questions and offers come straight to
              you rather than through an agent relay.{' '}
              <Link to="/sell-without-an-agent/">See how selling without an agent works →</Link>
            </span>
          </li>
        </ul>
      </section>

      <section className="region-cta">
        <h2>Ready to stop paying to sell?</h2>
        <p>List your home on Keyzee in a few minutes. Free forever, no card required.</p>
        <div className="region-cta__actions">
          <Link className="primary-button pill-action" to="/sell/new">
            List your home free <LuArrowRight aria-hidden="true" />
          </Link>
          <Link className="secondary-button pill-action" to="/house-valuation/">
            Get a free valuation
          </Link>
        </div>
      </section>

      <section className="sell-guide-faq">
        <div className="sell-guide-shell">
          <h2>{name} alternatives — common questions</h2>
          <div className="sell-guide-faq__grid">
            {faqs.map((item) => (
              <article key={item.q} className="sell-guide-faq__item">
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="region-section" aria-label="Other comparisons">
        <h2>Compare Keyzee with other platforms</h2>
        <ul className="region-index-list">
          {others.map((c) => (
            <li key={c.slug}>
              <Link to={`/${c.slug}-alternative/`}>
                <span className="region-index-name">{c.name} alternative</span>
              </Link>
            </li>
          ))}
          <li>
            <Link to="/compare-estate-agents/">
              <span className="region-index-name">All online estate agents compared</span>
            </Link>
          </li>
        </ul>
      </section>
    </>
  )
}

export default CompareAlternativePage
